<template>
    <div :class="{ 'pointer-events-none opacity-40': isGenerating }">
        <!--
            The row itself is the way into the editor on a phone, with no link beside it: the label
            said "Naam/tekst" and the link said "Toevoegen", which is one thing named twice — and the
            sheet's own green button is the call to action. From `md` up the list is already open
            below, so there is nothing to open.
        -->
        <component
            :is="showEditorCta ? 'button' : 'div'"
            :type="showEditorCta ? 'button' : undefined"
            class="flex w-full items-center gap-2.5 text-left"
            :class="{ 'cursor-pointer': showEditorCta }"
            @click="showEditorCta ? openTextEditor() : undefined"
        >
            <span class="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <TypeIcon class="size-3.5 text-primary" aria-hidden="true" />
            </span>

            <span class="min-w-0 flex-1 text-base text-gray-700 md:text-sm">
                {{ $t('pages.product_detail_page.ai_upload.personalization.field_label') }}
            </span>

            <ChevronRightIcon v-if="showEditorCta" class="size-4 shrink-0 text-gray-300" aria-hidden="true" />
        </component>

        <!--
            From `md` up the panel sits beside the gallery, so the list and its styling controls fit
            inline once opened. On a phone the CTA opens the full-height editor instead (see
            useAiTextEditorLauncher) — nothing renders here for that case.
        -->
        <div v-if="useInlineEditing && store.isOpen" class="mt-3 space-y-2">
            <AiTextItemList variant="inline" :is-read-only="isGenerating || isViewingEarlierVersion" />

            <!--
                Baked text has no styling controls and no save button, so without this the panel is a
                read-only line and nothing else — the customer who came here to change a letter has
                no way on. It cannot be edited in place (it is in the artwork by now), so the offer is
                the one thing that can happen: take it off and hand the same text back. The sentence
                that used to explain that above the button is gone — the button says it.
            -->
            <button
                v-if="isTextBaked && !isGenerating && !isViewingEarlierVersion"
                type="button"
                class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1.5 rounded-md border border-primary bg-white px-4 text-base font-semibold text-primary transition hover:bg-primary/5 md:h-auto md:py-2 md:text-sm"
                @click="onEditBakedTextClick"
            >
                <PencilIcon class="size-4" aria-hidden="true" />
                {{ $t('pages.product_detail_page.ai_upload.personalization.edit_baked_text') }}
            </button>

            <!--
                A phone reaches this through the sheet, where it is the one thing to press; the
                `md` side panel keeps the button's own size, since the column is narrow and the
                pointer is precise.
            -->
            <Button
                v-if="canGenerate || isSubmitting"
                task="updatePreview"
                class="h-14 text-base md:h-auto md:text-sm"
                :disabled="!canGenerate && !isSubmitting"
                :loading="isSubmitting"
                @click="onUpdatePreview"
            />

            <button
                v-if="session.hasPrePersonalizationBeautyShots && !isGenerating && !isViewingEarlierVersion"
                type="button"
                class="inline-flex w-full cursor-pointer items-center justify-center gap-x-1.5 rounded-md px-3 py-2 text-base text-gray-400 transition hover:text-gray-600 md:text-sm"
                @click="onRemoveAllText"
            >
                <RotateCcwIcon class="size-3.5" aria-hidden="true" />
                {{ $t('pages.product_detail_page.ai_upload.personalization.remove_all_text') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import { useAiPersonalizationControls } from '@/composables/ai-upload/useAiPersonalizationControls';
import { useAiTextEditorLauncher } from '@/composables/ai-upload/useAiTextEditorLauncher';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import AiTextItemList from '@/pages/ai-product-page/partials/ai-product-info/partials/AiTextItemList.vue';
import { useAiPersonalizationScope } from '@/composables/ai-upload/useAiPersonalizationScope';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';
import type { PersonalizationZone } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import type { AiPersonalizationPageProps, PersonalizationTextItem } from '@/stores/ai-upload/AiPersonalizationStore';
import { usePage } from '@inertiajs/vue3';
import { ChevronRightIcon, PencilIcon, RotateCcwIcon, TypeIcon } from '@lucide/vue';
import { computed, onMounted, ref, watch } from 'vue';

type TextPersonalizationBlockProps = {
    disabled?: boolean;
};

const props = withDefaults(defineProps<TextPersonalizationBlockProps>(), {
    disabled: false,
});

const { store, session, canGenerate, isSubmitting, onAddItem, onReseedPlaceholder, onUpdatePreview, onRemoveAllText } =
    useAiPersonalizationControls();
const page = usePage<AiPersonalizationPageProps>();
const historyStore = useAiGenerationHistoryStore();

/*
 * The panel shows the text of the version on screen — an earlier one's is loaded when it is picked,
 * and the live generation's comes back when it is. See useAiPersonalizationScope, which is mounted
 * here because this block is the panel that reads it.
 */
useAiPersonalizationScope();

/** An earlier version is a record of what was made then: its text reads back, and changes nothing. */
const isViewingEarlierVersion = computed<boolean>(() => historyStore.selectedEntryId !== undefined);
const { isTabletUp } = useAppearanceComposable();
const { openTextEditor, editBakedText } = useAiTextEditorLauncher();

function onEditBakedTextClick(): void {
    void editBakedText();
}

const isGenerating = computed<boolean>(() => props.disabled || isSubmitting.value);

const isMounted = ref(false);
onMounted(() => {
    isMounted.value = true;
});

/**
 * isMounted guard: SSR uses provideSSRWidth(1280) so isTabletUp is true there.
 *
 * Baked text takes the inline list on a phone too. The full editor is a keyboard, fonts, sizes,
 * colours and a save button, and none of that can change a name that is already in the artwork. The
 * inline list is what that state actually needs: the line read back as ordered, with the way to take
 * it off below it.
 */
const isTextBaked = computed<boolean>(
    () => store.items.length > 0 && store.items.every((item: PersonalizationTextItem) => store.isItemBaked(item.id)),
);

const useInlineEditing = computed<boolean>(() => !isMounted.value || isTabletUp.value || isTextBaked.value);

/**
 * The CTA is the phone's way into the text: the full-height editor while there is something to
 * type, and the read-back row once the name is in the artwork (openTextEditor stops there by
 * itself). From `md` up the list and its controls are already open beside the gallery, so the row
 * would only point at what is on screen.
 *
 * Never on an earlier version, though. Its text is a record of what was made then, and the editor
 * goes back to the live generation to open — so the row that was pressed said one name and the
 * editor showed another, which is the reported bug. Picking the newest thumbnail is the way back to
 * text that can still be changed.
 *
 * isMounted guard: SSR renders at 1280 (provideSSRWidth), so the row is out of the server HTML and
 * cannot flash in before the breakpoint is known.
 */
const showEditorCta = computed<boolean>(() => isMounted.value && !isTabletUp.value && !isViewingEarlierVersion.value);

/**
 * From `md` up the block is the panel's permanent text section, so a closed state there is a label
 * with nothing under it and no CTA to get it back. Reconciled rather than assumed: sessionStorage
 * carries the flag over from a narrower viewport, and a commit closes it on a phone.
 */
watch(
    [isMounted, isTabletUp],
    ([mounted, tabletUp]: [boolean, boolean]) => {
        if (!mounted || !tabletUp) {
            return;
        }

        store.isOpen = true;
    },
    { immediate: true },
);

// A committed generation bakes the text into the artwork and collapses the block back to its CTA —
// there is nothing left to expand until the customer taps "Bewerken" again.
watch(
    () => store.isCommitted,
    (committed: boolean) => {
        if (!committed) {
            return;
        }

        store.expandedId = undefined;

        if (isTabletUp.value) {
            return;
        }

        store.isOpen = false;
    },
);

/**
 * This block only mounts on the with-text product once the customer has a generation, so reaching
 * here means adding text is the point of the product they chose. Seeds a first item placed over the
 * same spot the placeholder occupies on the gallery images, which saves the customer having to find
 * the add button to do the thing they came for.
 *
 * Guarded on all three so a returning visitor's own text is never disturbed: anything already
 * committed, still pending, or an open panel means they have been here before.
 *
 * On a phone it only seeds state and never opens the panel: the editor there covers the lower half
 * of the screen, so opening it by itself would bury the result the customer just waited for behind
 * something they did not ask for. From `md` up the panel sits beside the picture and hides nothing,
 * so the line is handed over open — typing a name is what the product is for, and a collapsed row
 * makes the customer ask for it first.
 */
onMounted(() => {
    if (store.isOpen || store.hasItems() || store.isCommitted) {
        openLineOnDesktop();

        return;
    }

    onAddItem();
    openLineOnDesktop();
});

function openLineOnDesktop(): void {
    const firstItemId = store.items[0]?.id;

    if (!isTabletUp.value || firstItemId === undefined || store.isCommitted) {
        return;
    }

    store.openItemForEditing(firstItemId);
}

/**
 * What a new line starts as arrives deferred, and can land after the line has been seeded.
 *
 * The reported bug: leave the with-text product mid-generation, come back to it, and the name came
 * out in Roboto instead of the synced face. Returning to a product that already has a generation
 * mounts this block in the page's first render, before the deferred group is in — so the placeholder
 * was built from the store's own fallbacks, and then persisted per SKU, which is why it survived
 * every later visit.
 *
 * Rebuilt when the real defaults show up. Untouched lines only; see onReseedPlaceholder.
 */
watch(
    () => page.props.personalization,
    (defaults: AiPersonalizationPageProps['personalization']) => {
        if (defaults === undefined || defaults === null) {
            return;
        }

        onReseedPlaceholder();
    },
);

/**
 * And again when the print area is known.
 *
 * The placeholder's size and its place down the artwork are both a fraction of that area, so a line
 * seeded before it is known gets the store's blanket fallback: a name half the size the template
 * asks for, in the middle of the preview rather than near the top of the print area. That is what a
 * customer's first generation starts with — the upload clears the batch, and there is no earlier one
 * to borrow a zone from, so the line the panel seeds at that moment has nothing to scale against.
 *
 * Immediate, and falling back to the canonical zone, because by the time this block exists the zone
 * is usually already known: it only mounts once a generation is on screen, which is after the slots
 * (and their zones) have landed. Waiting for the zone to *change* from here meant the correction
 * never ran at all, and the line kept its fallback size and place until something else happened to
 * move the zone — a later generation, or a reload.
 *
 * Untouched lines only, so a customer's own placing is never moved under them — see
 * onReseedPlaceholder.
 */
watch(
    () => session.displayZone ?? session.canonicalZone,
    (zone: PersonalizationZone | undefined) => {
        if (zone === undefined) {
            return;
        }

        onReseedPlaceholder();
    },
    { immediate: true },
);
</script>
